import {observer} from 'mobx-react-lite';
import {Form, Formik, FormikProps} from 'formik';
import {SectionDialogProps} from '../../../../entity/props/title/section-dialog-props';
import {
  Button, Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select, SelectChangeEvent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import {useService} from '../../../../infrastructure/service/service';
import {AddSectionDto} from '../../../../entity/dto/add-section-dto';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import React from 'react';

import '../../../../assets/css/style.css';
import '../../../../assets/css/title/section-dialog.css';

const SectionDialog = (props: SectionDialogProps): JSX.Element => {
  const {sectionService} = useService();

  const handleValidate = (values: AddSectionDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (values.sections.length === 0)
      errors.sections = 'At least one section is required.';

    return errors;
  }

  return (
    <Dialog open={props.open}>
      <DialogTitle className={'zitles-dialog-title'}>
        <span>Add New Sections</span>
        <IconButton onClick={(): void => props.setOpen(false)}>
          <CloseIcon className={'dialog-icon'}/>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={'section-dialog-content'}>
        <Formik
          initialValues={sectionService.getAddNewSectionDto()}
          validate={handleValidate}
          onSubmit={props.handleSubmit}>
          {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue}: FormikProps<AddSectionDto>): JSX.Element => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <FormControl required fullWidth error={touched.sections && Boolean(errors.sections)}>
                    <InputLabel>Sections</InputLabel>
                    <Select
                      id={'sections-input'}
                      className={'multiselect-dropdown'}
                      multiple
                      name='sections'
                      value={values.sections}
                      label='Sections'
                      onBlur={handleBlur}
                      renderValue={(selected: any[]): JSX.Element => (
                        <Box className={'section-chip'}>
                          {
                            selected.map((value: any): JSX.Element =>
                              <Chip
                                className={'zitles-chip'}
                                key={value}
                                label={value}
                                variant={'outlined'}
                                onMouseDown={(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => event.stopPropagation()}
                                onDelete={(event: any): void => {
                                  event.stopPropagation();
                                  event.preventDefault();

                                  const newValues = values.sections?.filter((s: any): boolean => s !== value);
                                  setFieldValue('sections', newValues);
                                }}/>
                            )
                          }
                        </Box>
                      )}
                      onChange={(event: SelectChangeEvent<any[]>) => handleChange({target: {name: event.target.name, value: event.target.value}})}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            overflowY: 'auto'
                          }
                        }
                      }}>
                      {
                        props.sectionOptions
                          .filter((option: DropdownOption): boolean => !values.sections?.includes(option.value))
                          .map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                      }
                    </Select>
                    <FormHelperText>{touched.sections && typeof errors.sections === 'string' && errors.sections}</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2, mx: -3 }} />
              <Box className={'action-btn-container'}>
                <Button
                  id={'section-cancel-btn'}
                  disableTouchRipple
                  onClick={(): void => props.setOpen(false)}
                  className={'zitles-button cancel'}>
                  Cancel
                </Button>
                <Button
                  id={'section-submit-btn'}
                  type='submit'
                  disabled={isSubmitting}
                  className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
                  startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
                  {
                    isSubmitting ? 'Saving' : 'Save Section(s)'
                  }
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default observer(SectionDialog);
